import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Loader2,
  RefreshCw, Download, Printer, Send, AlertTriangle, X, Users,
  Settings2, History, ExternalLink, FileSpreadsheet, CheckCircle2,
  MoreVertical, Search, TableProperties, FileText,
} from 'lucide-react';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API } from '../utils/apiConfig';
import { useMasterData } from '../contexts/MasterDataContext';

// ── constants ─────────────────────────────────────────────────────────────────
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const STAFF_STATUSES = ['active', 'inactive', 'resigned', 'not_joined'];
const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-amber-100 text-amber-700',
  resigned: 'bg-rose-100 text-rose-700',
  not_joined: 'bg-slate-100 text-slate-500',
};
const AUTO_SAVE_DELAY = 1200;

const err = (e) => e?.response?.data?.detail || e?.message || 'Error';

// ── date helpers ──────────────────────────────────────────────────────────────
function getMondayOfWeek(d = new Date()) {
  const diff = d.getDay() === 0 ? -6 : 1 - d.getDay();
  const m = new Date(d); m.setDate(d.getDate() + diff); m.setHours(0, 0, 0, 0); return m;
}
function addDays(d, n) { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; }
function toISO(d) { return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`; }
function p2(n) { return String(n).padStart(2, '0'); }
function fmtShort(d) { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
function fmtWeekRange(ws) {
  if (!ws) return '';
  return `${fmtShort(ws)} – ${fmtShort(addDays(new Date(ws), 6))}`;
}
function currentMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}`;
}
function currentMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-01`;
}
function currentMonthEnd() {
  const d = new Date();
  return toISO(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
export default function DutyRosterPage() {
  const { getVenueNames } = useMasterData();
  const venueNames = useMemo(() => getVenueNames() || [], [getVenueNames]);

  const [tab, setTab] = useState('roster');
  const [weekStart, setWeekStart] = useState(toISO(getMondayOfWeek()));

  // Roster
  const [roster, setRoster] = useState(null);
  const [rosterList, setRosterList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const autoSaveRef = useRef(null);

  // Cell editing
  const [editCell, setEditCell] = useState(null);
  const [cellForm, setCellForm] = useState({ entry_type: 'shift', shift_text: '', leave_reason: '', hours: '' });

  // Staff
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [monthlyHours, setMonthlyHours] = useState({});

  // Leaves
  const [leaves, setLeaves] = useState([]);
  const [leaveDateFrom, setLeaveDateFrom] = useState(currentMonthStart());
  const [leaveDateTo, setLeaveDateTo] = useState(currentMonthEnd());

  // Settings
  const [settings, setSettings] = useState({ closing_time: '00:30' });

  // Roster grid filters
  const [filterVenue, setFilterVenue] = useState('all');
  const [filterStaff, setFilterStaff] = useState('all');
  const [filterDesignation, setFilterDesignation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [searchStaff, setSearchStaff] = useState('');

  // ── derived ─────────────────────────────────────────────────────────────────
  const staffMap = useMemo(() => {
    const m = {};
    staffList.forEach((s) => { m[s.id] = s; });
    return m;
  }, [staffList]);

  const entryLookup = useMemo(() => {
    const m = {};
    (roster?.entries || []).forEach((e) => {
      if (!m[e.staff_id]) m[e.staff_id] = {};
      m[e.staff_id][e.day] = e;
    });
    return m;
  }, [roster?.entries]);

  const summary = useMemo(() => {
    const s = {};
    (roster?.entries || []).forEach((e) => {
      const sid = e.staff_id;
      if (!s[sid]) s[sid] = { working_days: 0, weekly_offs: 0, leave_days: 0, total_hours: 0 };
      if (e.entry_type === 'shift') { s[sid].working_days++; s[sid].total_hours += parseFloat(e.hours || 0); }
      else if (e.entry_type === 'weekoff') s[sid].weekly_offs++;
      else if (e.entry_type === 'leave') s[sid].leave_days++;
    });
    return s;
  }, [roster?.entries]);

  // Week days with dates
  const weekDays = useMemo(() => {
    if (!weekStart) return DAYS.map((d) => ({ key: d, label: DAY_LABELS[d], date: '' }));
    return DAYS.map((d, i) => ({
      key: d, label: DAY_LABELS[d], date: fmtShort(addDays(new Date(weekStart), i)),
    }));
  }, [weekStart]);

  // Filter staff for roster grid
  const filteredStaff = useMemo(() => {
    return staffList.filter((s) => {
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      if (filterVenue !== 'all') {
        // Check home venue OR any shift entry assigned to this venue this week
        const hasVenueInRoster = Object.values(entryLookup[s.id] || {}).some(
          (e) => e.entry_type === 'shift' && e.venue === filterVenue
        );
        if (s.venue !== filterVenue && !hasVenueInRoster) return false;
      }
      if (filterDesignation !== 'all' && s.designation !== filterDesignation) return false;
      if (filterStaff !== 'all' && s.id !== filterStaff) return false;
      if (searchStaff) {
        const q = searchStaff.toLowerCase();
        if (!s.name.toLowerCase().includes(q) && !(s.mobile || '').includes(q)) return false;
      }
      return true;
    });
  }, [staffList, filterVenue, filterDesignation, filterStatus, filterStaff, searchStaff, entryLookup]);

  // Unique designations for filter
  const designations = useMemo(() => [...new Set(staffList.map((s) => s.designation).filter(Boolean))].sort(), [staffList]);

  // ── loaders ──────────────────────────────────────────────────────────────────
  const loadRoster = useCallback(async (ws) => {
    const w = ws || weekStart;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/duty-roster/rosters/${w}`);
      setRoster(r.data.roster);
      setPendingChanges(false);
    } catch (e) {
      if (e?.response?.status === 404) setRoster(null);
      else toast.error(err(e));
    } finally { setLoading(false); }
  }, [weekStart]);

  const loadStaff = useCallback(async () => {
    setStaffLoading(true);
    try {
      const r = await axios.get(`${API}/duty-roster/staff`);
      setStaffList(r.data.staff || []);
    } catch (e) { toast.error(err(e)); }
    finally { setStaffLoading(false); }
  }, []);

  const loadLeaves = useCallback(async () => {
    const params = {};
    if (leaveDateFrom) params.date_from = leaveDateFrom;
    if (leaveDateTo) params.date_to = leaveDateTo;
    try {
      const r = await axios.get(`${API}/duty-roster/leaves`, { params });
      setLeaves(r.data.leaves || []);
    } catch (e) { toast.error(err(e)); }
  }, [leaveDateFrom, leaveDateTo]);

  const loadSettings = useCallback(async () => {
    try { const r = await axios.get(`${API}/duty-roster/settings`); setSettings(r.data); }
    catch {}
  }, []);

  const loadRosterList = useCallback(async () => {
    try { const r = await axios.get(`${API}/duty-roster/rosters`); setRosterList(r.data.rows || []); }
    catch {}
  }, []);

  const loadMonthlyHours = useCallback(async (month = currentMonthISO()) => {
    try {
      const r = await axios.get(`${API}/duty-roster/staff-monthly-hours`, { params: { month } });
      setMonthlyHours(r.data.hours_by_staff || {});
    } catch {}
  }, []);

  useEffect(() => { loadRoster(); loadStaff(); loadSettings(); }, [weekStart]);
  useEffect(() => { loadRosterList(); }, [weekStart]);
  useEffect(() => {
    if (tab === 'leaves') { loadLeaves(); loadStaff(); }
    if (tab === 'staff') { loadStaff(); loadMonthlyHours(); }
    if (tab === 'settings') loadSettings();
  }, [tab]);

  // ── auto-save ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pendingChanges) return;
    const capturedStatus = roster?.status;   // capture before async delay
    clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(async () => {
      setAutoSaving(true);
      try {
        const res = await axios.post(`${API}/duty-roster/rosters`, {
          week_start: weekStart,
          entries: roster?.entries || [],
        });
        const updatedWarnings = res.data.warnings || [];
        const wasPublished = capturedStatus === 'published';
        if (wasPublished) {
          // Re-publish immediately to keep roster published after edit
          await axios.post(`${API}/duty-roster/rosters/${weekStart}/publish`);
          setRoster((prev) => prev ? { ...prev, warnings: updatedWarnings, status: 'published' } : prev);
        } else {
          setRoster((prev) => prev ? { ...prev, warnings: updatedWarnings } : prev);
        }
        setPendingChanges(false);
      } catch { /* silent */ }
      finally { setAutoSaving(false); }
    }, AUTO_SAVE_DELAY);
    return () => clearTimeout(autoSaveRef.current);
  }, [roster?.entries, pendingChanges, weekStart]);

  // ── week nav ──────────────────────────────────────────────────────────────
  const prevWeek = () => setWeekStart((ws) => toISO(addDays(new Date(ws), -7)));
  const nextWeek = () => setWeekStart((ws) => toISO(addDays(new Date(ws), 7)));

  // ── cell editing ──────────────────────────────────────────────────────────
  const openCell = (staffId, day) => {
    const ex = entryLookup[staffId]?.[day];
    setEditCell({ staffId, day });
    setCellForm({
      entry_type: ex?.entry_type || 'shift',
      shift_text: ex?.shift_text || '',
      leave_reason: ex?.leave_reason || '',
      hours: ex?.hours ?? '',
      venue: ex?.venue || '',
    });
  };

  const applyCell = () => {
    if (!editCell) return;
    const { staffId, day } = editCell;
    if (cellForm.entry_type === 'weekoff' && ['fri', 'sat', 'sun'].includes(day)) {
      toast.error('Weekly off only allowed Mon–Thu');
      return;
    }
    upsertEntry({
      staff_id: staffId, day,
      entry_type: cellForm.entry_type,
      shift_text: cellForm.entry_type === 'shift' ? cellForm.shift_text : '',
      hours: cellForm.entry_type === 'shift' ? (parseFloat(cellForm.hours) || 0) : 0,
      leave_reason: cellForm.entry_type === 'leave' ? cellForm.leave_reason : '',
      venue: cellForm.entry_type === 'shift' ? (cellForm.venue || '') : '',
      shift_start: '', shift_end: '',
    });
    setEditCell(null);
  };

  const upsertEntry = (newEntry) => {
    setRoster((prev) => {
      const base = prev || { week_start: weekStart, week_end: toISO(addDays(new Date(weekStart), 6)), status: 'draft', entries: [], warnings: [] };
      const filtered = (base.entries || []).filter(
        (e) => !(e.staff_id === newEntry.staff_id && e.day === newEntry.day)
      );
      return { ...base, entries: [...filtered, newEntry] };
    });
    setPendingChanges(true);
  };

  const updateEntryVenue = (staffId, day, venue) => {
    setRoster((prev) => {
      if (!prev) return prev;
      const entries = (prev.entries || []).map((e) =>
        e.staff_id === staffId && e.day === day ? { ...e, venue } : e
      );
      return { ...prev, entries };
    });
    setPendingChanges(true);
  };

  const clearCell = (staffId, day) => {
    setRoster((prev) => prev ? {
      ...prev,
      entries: (prev.entries || []).filter((e) => !(e.staff_id === staffId && e.day === day)),
    } : prev);
    setPendingChanges(true);
  };

  // ── actions ───────────────────────────────────────────────────────────────
  const generateNext = async () => {
    if (!window.confirm('Generate next week\'s roster from this week? This will create a draft you can edit.')) return;
    try {
      const r = await axios.post(`${API}/duty-roster/generate-next-week`,
        roster ? { base_week_start: weekStart } : {}
      );
      const nextWs = r.data.roster?.week_start;
      if (nextWs) {
        setWeekStart(nextWs);
        setRoster(r.data.roster);
        setPendingChanges(false);
        const w = r.data.warnings || [];
        if (w.length) toast.warning(`Generated with ${w.length} warning(s). Review before publishing.`);
        else toast.success(`Next week roster generated`);
      }
    } catch (e) { toast.error(err(e)); }
  };

  const publishRoster = async () => {
    if (!roster) { await saveDraftNow(); return; }
    if (roster.status === 'published') {
      try {
        await axios.post(`${API}/duty-roster/rosters/${weekStart}/unpublish`);
        await loadRoster(); loadLeaves();
        toast.success('Moved back to Draft');
      } catch (e) { toast.error(err(e)); }
      return;
    }
    if (roster.warnings?.length && !window.confirm(`${roster.warnings.length} warning(s) exist. Publish anyway?`)) return;
    try {
      await saveDraftNow(true); // ensure saved
      await axios.post(`${API}/duty-roster/rosters/${weekStart}/publish`);
      await loadRoster(); loadLeaves();
      toast.success('Roster published');
    } catch (e) { toast.error(err(e)); }
  };

  const saveDraftNow = async (silent = false) => {
    try {
      clearTimeout(autoSaveRef.current);
      await axios.post(`${API}/duty-roster/rosters`, {
        week_start: weekStart,
        entries: roster?.entries || [],
      });
      setPendingChanges(false);
      if (!silent) toast.success('Draft saved');
      await loadRoster();
    } catch (e) { if (!silent) toast.error(err(e)); }
  };

  const exportExcel = () => {
    const headers = ['#', 'Employee', 'Designation',
      ...weekDays.map((d) => `${d.label} ${d.date} - Shift`),
      ...weekDays.map((d) => `${d.label} ${d.date} - Venue`),
      'Working Days', 'Offs', 'Leaves', 'Hours'];
    const rows = filteredStaff.map((s, i) => {
      const sum = summary[s.id] || {};
      return [
        i + 1, s.name, s.designation || '',
        ...weekDays.map((d) => {
          const e = entryLookup[s.id]?.[d.key];
          if (!e) return '';
          if (e.entry_type === 'weekoff') return 'WEEKOFF';
          if (e.entry_type === 'leave') return 'LEAVE';
          return e.shift_text || '';
        }),
        ...weekDays.map((d) => entryLookup[s.id]?.[d.key]?.venue || ''),
        sum.working_days || 0, sum.weekly_offs || 0,
        sum.leave_days || 0, sum.total_hours || 0,
      ];
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, 'Duty Roster');
    XLSX.writeFile(wb, `DutyRoster_${weekStart}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="flex flex-col h-full bg-slate-50" data-testid="duty-roster-page">
        <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 min-h-0">
          {/* ── Top bar ── */}
          <div className="bg-white border-b border-slate-200 px-4 py-3 print:hidden">
            <div className="flex flex-wrap items-center gap-3">
              {/* Title */}
              <div className="mr-2">
                <div className="flex items-center gap-2 text-slate-900">
                  <TableProperties className="w-5 h-5 text-indigo-600" />
                  <span className="font-bold text-base">Weekly Staff Duty Roster</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 ml-7" data-testid="week-label">
                  {fmtWeekRange(weekStart)}
                </div>
              </div>

              {/* Week nav + Generate */}
              <div className="flex items-center gap-1 flex-wrap">
                <button onClick={prevWeek}
                  className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors"
                  data-testid="prev-week-btn">
                  <ChevronLeft className="w-4 h-4" /> Previous Week
                </button>

                <button onClick={generateNext}
                  className="flex items-center gap-2 text-sm font-bold bg-violet-600 hover:bg-violet-700 text-white rounded-lg px-4 py-1.5 shadow-sm transition-colors mx-1"
                  data-testid="generate-next-btn">
                  <Send className="w-3.5 h-3.5" /> Generate Next Week
                </button>

                <button onClick={nextWeek}
                  className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors"
                  data-testid="next-week-btn">
                  Next Week <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-1.5 ml-auto flex-wrap">
                {/* Auto-save indicator */}
                {autoSaving && <span className="text-xs text-amber-500 font-medium flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
                {pendingChanges && !autoSaving && <span className="text-xs text-amber-400">● Unsaved</span>}
                {roster?.status === 'published' && (
                  <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1" data-testid="roster-status-badge">
                    <CheckCircle2 className="w-3 h-3" /> Published
                  </span>
                )}

                <button onClick={saveDraftNow}
                  className="flex items-center gap-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors text-slate-600"
                  data-testid="save-draft-btn">
                  <FileText className="w-3.5 h-3.5" /> Save Draft
                </button>

                <button onClick={publishRoster}
                  className={`flex items-center gap-1 text-xs font-bold rounded-lg px-3 py-1.5 transition-colors ${
                    roster?.status === 'published'
                      ? 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                  data-testid="publish-btn">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {roster?.status === 'published' ? 'Unpublish' : 'Publish'}
                </button>

                <button onClick={exportExcel}
                  className="flex items-center gap-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors text-slate-600"
                  data-testid="export-excel-btn">
                  <Download className="w-3.5 h-3.5" /> Export Excel
                </button>

                <button onClick={() => window.print()}
                  className="flex items-center gap-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors text-slate-600"
                  data-testid="print-btn">
                  <Printer className="w-3.5 h-3.5" /> Print
                </button>
              </div>
            </div>

            {/* Tabs row */}
            <div className="mt-2">
              <TabsList className="h-auto gap-0.5 bg-slate-100 p-0.5 rounded-lg">
                {[
                  { key: 'roster', icon: CalendarDays, label: 'Weekly Roster' },
                  { key: 'leaves', icon: History, label: 'Leave History' },
                  { key: 'staff', icon: Users, label: 'Staff Master' },
                  { key: 'salary', icon: FileSpreadsheet, label: 'Salary Report' },
                  { key: 'settings', icon: Settings2, label: 'Settings' },
                ].map(({ key, icon: Icon, label }) => (
                  <TabsTrigger key={key} value={key}
                    className="text-xs px-3 py-1.5 gap-1"
                    data-testid={`tab-${key}`}>
                    <Icon className="w-3 h-3" /> {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>

          {/* ═══════════════════ ROSTER TAB ═══════════════════ */}
          {tab === 'roster' && (
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {/* Filter row */}
              <div className="bg-white border-b border-slate-200 px-4 py-2.5 flex flex-wrap items-center gap-2 print:hidden">
                <select value={filterVenue} onChange={(e) => setFilterVenue(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  data-testid="filter-venue">
                  <option value="all">All Venues</option>
                  {venueNames.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>

                <select value={filterStaff} onChange={(e) => setFilterStaff(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  data-testid="filter-staff">
                  <option value="all">All Staff</option>
                  {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>

                <select value={filterDesignation} onChange={(e) => setFilterDesignation(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  data-testid="filter-designation">
                  <option value="all">All Designations</option>
                  {designations.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>

                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  data-testid="filter-status">
                  <option value="all">All Status</option>
                  {STAFF_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>

                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={searchStaff} onChange={(e) => setSearchStaff(e.target.value)}
                    placeholder="Search staff…"
                    className="text-xs pl-7 pr-3 py-1.5 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300 w-[160px]"
                    data-testid="search-staff" />
                </div>

                {/* Legend */}
                <div className="ml-auto flex items-center gap-3">
                  {[
                    { label: 'Working', bg: 'bg-amber-100', border: 'border-amber-300' },
                    { label: 'WEEKOFF', bg: 'bg-indigo-600', border: 'border-indigo-600', text: 'text-white' },
                    { label: 'LEAVE', bg: 'bg-rose-100', border: 'border-rose-300', text: 'text-rose-600' },
                  ].map(({ label, bg, border, text = 'text-slate-700' }) => (
                    <div key={label} className="flex items-center gap-1.5">
                      <div className={`w-5 h-3.5 rounded ${bg} border ${border}`} />
                      <span className="text-xs text-slate-600">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Warnings */}
              {(roster?.warnings?.length > 0) && (
                <div className="mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 print:hidden"
                  data-testid="roster-warnings">
                  <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {roster.warnings.length} Warning{roster.warnings.length > 1 ? 's' : ''}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                    {roster.warnings.map((w, i) => (
                      <span key={i} className="text-xs text-amber-700">• {w}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Grid */}
              {loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
                </div>
              ) : (
                <div className="flex-1 overflow-auto px-4 py-3">
                  <RosterGrid
                    weekDays={weekDays}
                    filteredStaff={filteredStaff}
                    entryLookup={entryLookup}
                    summary={summary}
                    venueNames={venueNames}
                    onCellClick={openCell}
                    onClearCell={clearCell}
                    onVenueChange={updateEntryVenue}
                  />
                  {filteredStaff.length === 0 && !loading && (
                    <div className="text-center py-16 text-slate-400">
                      <Users className="w-10 h-10 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">No staff found. Add staff in the Staff Master tab.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Past rosters */}
              {rosterList.length > 0 && (
                <div className="px-4 py-2 bg-white border-t border-slate-200 flex flex-wrap gap-2 print:hidden">
                  <span className="text-xs text-slate-400 self-center">Past:</span>
                  {rosterList.slice(0, 6).map((r) => (
                    <button key={r.week_start} onClick={() => setWeekStart(r.week_start)}
                      className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                        r.week_start === weekStart
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400 hover:text-indigo-600'
                      }`}>
                      {fmtShort(r.week_start)}
                      <span className={`ml-1 ${r.status === 'published' ? 'text-emerald-400' : 'text-amber-400'}`}>●</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════ LEAVE HISTORY TAB ═══════════════════ */}
          {tab === 'leaves' && (
            <LeaveHistoryPanel
              leaves={leaves} staffList={staffList}
              dateFrom={leaveDateFrom} dateTo={leaveDateTo}
              onDateFromChange={setLeaveDateFrom}
              onDateToChange={setLeaveDateTo}
              onRefresh={loadLeaves}
            />
          )}

          {/* ═══════════════════ STAFF MASTER TAB ═══════════════════ */}
          {tab === 'staff' && (
            <StaffMasterPanel
              staffList={staffList} loading={staffLoading}
              venueNames={venueNames} monthlyHours={monthlyHours}
              onChanged={() => { loadStaff(); loadMonthlyHours(); }}
            />
          )}

          {/* ═══════════════════ SALARY REPORT TAB ═══════════════════ */}
          {tab === 'salary' && <SalaryReportPanel venueNames={venueNames} />}

          {/* ═══════════════════ SETTINGS TAB ═══════════════════ */}
          {tab === 'settings' && (
            <SettingsPanel settings={settings} onSaved={setSettings} />
          )}
        </Tabs>
      </div>

      {/* Cell edit dialog */}
      {editCell && (
        <CellEditDialog
          staffId={editCell.staffId} day={editCell.day}
          staffMap={staffMap} venueNames={venueNames}
          form={cellForm} setForm={setCellForm}
          onApply={applyCell} onClose={() => setEditCell(null)}
        />
      )}
    </DashboardLayout>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROSTER GRID
// ══════════════════════════════════════════════════════════════════════════════

function RosterGrid({ weekDays, filteredStaff, entryLookup, summary, venueNames, onCellClick, onClearCell, onVenueChange }) {
  return (
    <div className="rounded-xl border border-slate-200 shadow-sm overflow-hidden print:shadow-none bg-white"
      data-testid="roster-grid">
      <table className="w-full text-xs border-collapse" style={{ minWidth: 900 }}>
        <thead>
          <tr className="bg-slate-800 text-white select-none">
            <th className="sticky left-0 z-20 bg-slate-800 w-8 px-2 py-3 text-center font-semibold border-r border-slate-600">#</th>
            <th className="sticky left-8 z-20 bg-slate-800 px-3 py-3 text-left font-semibold border-r border-slate-600 min-w-[150px]">
              <div className="text-[11px]">Employee Name</div>
              <div className="text-[9px] text-slate-400 font-normal">Designation</div>
            </th>
            {weekDays.map((d) => (
              <th key={d.key} className={`px-2 py-3 text-center font-semibold border-r border-slate-600 min-w-[120px] ${['sat','sun'].includes(d.key) ? 'bg-slate-700' : ''}`}>
                <div>{d.label}</div>
                <div className="text-[9px] text-slate-400 font-normal">{d.date}</div>
              </th>
            ))}
            <th className="px-2 py-3 text-center font-semibold bg-slate-700 min-w-[72px]">Summary</th>
            <th className="w-8 bg-slate-700" />
          </tr>
        </thead>
        <tbody>
          {filteredStaff.map((staff, idx) => {
            const sum = summary[staff.id] || {};
            return (
              <tr key={staff.id}
                className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-blue-50/20 transition-colors`}
                data-testid={`roster-row-${staff.id}`}>

                {/* Row number */}
                <td className="sticky left-0 z-10 bg-inherit w-8 px-2 py-2 text-center text-slate-400 font-medium border-r border-slate-100">
                  {idx + 1}
                </td>

                {/* Staff info */}
                <td className="sticky left-8 z-10 bg-inherit px-3 py-2 border-r border-slate-100">
                  <div className="font-semibold text-slate-800">{staff.name}</div>
                  {staff.designation && <div className="text-[10px] text-slate-400">{staff.designation}</div>}
                </td>

                {/* Day cells */}
                {weekDays.map((d) => {
                  const entry = entryLookup[staff.id]?.[d.key];
                  const et = entry?.entry_type;
                  const isWeekend = ['sat', 'sun'].includes(d.key);
                  return (
                    <td key={d.key}
                      className={`px-1 py-1 border-r border-slate-100 align-top group relative ${isWeekend ? 'bg-slate-50/50' : ''}`}
                      data-testid={`cell-${staff.id}-${d.key}`}>
                      {/* Shift / badge area */}
                      <div
                        onClick={() => onCellClick(staff.id, d.key)}
                        className={`rounded-t text-[11px] font-medium text-center px-1 py-1.5 cursor-pointer min-h-[28px] transition-all ${
                          et === 'weekoff'
                            ? 'bg-indigo-600 text-white font-bold'
                            : et === 'leave'
                            ? 'bg-rose-100 text-rose-600 font-bold'
                            : et === 'shift'
                            ? 'bg-amber-50 text-slate-800 hover:bg-amber-100'
                            : 'border border-dashed border-slate-200 text-slate-300 hover:border-indigo-300 hover:text-indigo-300'
                        }`}>
                        {et === 'weekoff' && 'WEEKOFF'}
                        {et === 'leave' && 'LEAVE'}
                        {et === 'shift' && (entry.shift_text || <span className="opacity-40">+ shift</span>)}
                        {!et && <span className="text-[10px]">+</span>}
                        {/* Clear button */}
                        {entry && (
                          <button
                            onClick={(ev) => { ev.stopPropagation(); onClearCell(staff.id, d.key); }}
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-white rounded-full p-0.5 shadow text-slate-400 hover:text-rose-500 transition-opacity"
                            title="Clear">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                      {/* Venue dropdown */}
                      <select
                        value={entry?.venue || ''}
                        disabled={!entry || et !== 'shift'}
                        onChange={(ev) => onVenueChange(staff.id, d.key, ev.target.value)}
                        onClick={(ev) => ev.stopPropagation()}
                        className={`w-full text-[10px] rounded-b border-t-0 border border-slate-200 px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300 ${
                          (!entry || et !== 'shift') ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:border-slate-300'
                        }`}
                        data-testid={`venue-dropdown-${staff.id}-${d.key}`}>
                        <option value="">— venue —</option>
                        {venueNames.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </td>
                  );
                })}

                {/* Summary */}
                <td className="px-2 py-2 text-center bg-slate-50/50">
                  {sum.working_days !== undefined ? (
                    <div className="space-y-0.5">
                      <div className="text-emerald-600 font-bold text-xs">{sum.working_days}W</div>
                      <div className="text-indigo-600 text-[10px] font-semibold">{sum.weekly_offs}OFF</div>
                      {sum.leave_days > 0 && <div className="text-rose-500 text-[10px] font-semibold">{sum.leave_days}L</div>}
                    </div>
                  ) : <span className="text-slate-300">—</span>}
                </td>

                {/* Row menu */}
                <td className="px-1 py-2 text-center">
                  <button className="text-slate-300 hover:text-slate-600 transition-colors p-1 rounded hover:bg-slate-100"
                    title="Row actions"
                    data-testid={`row-menu-${staff.id}`}>
                    <MoreVertical className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CELL EDIT DIALOG
// ══════════════════════════════════════════════════════════════════════════════

function CellEditDialog({ staffId, day, staffMap, venueNames, form, setForm, onApply, onClose }) {
  const staff = staffMap[staffId] || {};
  const isWeekend = ['fri', 'sat', 'sun'].includes(day);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="cell-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{staff.name} — {DAY_LABELS[day]}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Type selector */}
          <div className="grid grid-cols-3 gap-2">
            {['shift', 'weekoff', 'leave'].map((et) => {
              const disabled = et === 'weekoff' && isWeekend;
              const active = form.entry_type === et;
              return (
                <button key={et} disabled={disabled}
                  onClick={() => !disabled && set('entry_type', et)}
                  className={`rounded-lg py-2 text-xs font-bold border-2 transition-all ${
                    active
                      ? et === 'weekoff' ? 'bg-indigo-600 border-indigo-600 text-white'
                      : et === 'leave' ? 'bg-rose-500 border-rose-500 text-white'
                      : 'bg-amber-400 border-amber-400 text-slate-900'
                      : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'
                  } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                  data-testid={`cell-type-${et}`}>
                  {et.toUpperCase()}
                  {disabled && <div className="text-[8px] opacity-60">Mon-Thu only</div>}
                </button>
              );
            })}
          </div>

          {form.entry_type === 'shift' && (
            <>
              <div>
                <Label className="text-xs">Shift</Label>
                <Input value={form.shift_text} onChange={(e) => set('shift_text', e.target.value)}
                  placeholder="e.g. 4pm to closing, 11am to 7pm" autoFocus
                  data-testid="shift-text-input" />
                <p className="text-[10px] text-slate-400 mt-0.5">Split shifts: 7am to 1pm/5pm to closing</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Hours (optional)</Label>
                  <Input type="number" min="0" step="0.5" value={form.hours}
                    onChange={(e) => set('hours', e.target.value)}
                    placeholder="Auto" data-testid="shift-hours-input" />
                </div>
                <div>
                  <Label className="text-xs">Venue</Label>
                  <select value={form.venue || ''} onChange={(e) => set('venue', e.target.value)}
                    className="w-full text-xs border border-slate-200 rounded-md px-2 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300"
                    data-testid="cell-venue-select">
                    <option value="">— Select —</option>
                    {venueNames.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}

          {form.entry_type === 'leave' && (
            <div>
              <Label className="text-xs">Leave Reason</Label>
              <Input value={form.leave_reason} onChange={(e) => set('leave_reason', e.target.value)}
                placeholder="Sick, Personal, etc." autoFocus data-testid="leave-reason-input" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={onApply}
            disabled={form.entry_type === 'weekoff' && isWeekend}
            data-testid="cell-apply-btn">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// LEAVE HISTORY PANEL
// ══════════════════════════════════════════════════════════════════════════════

function LeaveHistoryPanel({ leaves, staffList, dateFrom, dateTo, onDateFromChange, onDateToChange, onRefresh }) {
  const staffLookup = useMemo(() => {
    const m = {};
    (staffList || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [staffList]);

  // Leave counts per staff for the filtered period
  const leaveCounts = useMemo(() => {
    const c = {};
    leaves.forEach((l) => {
      const sid = l.staff_id;
      c[sid] = (c[sid] || 0) + 1;
    });
    return c;
  }, [leaves]);

  const enriched = useMemo(() => leaves.map((l) => ({
    ...l,
    staff_name: (staffLookup[l.staff_id] || {}).name || '(deleted)',
    venue: (staffLookup[l.staff_id] || {}).venue || '',
  })), [leaves, staffLookup]);

  const staffWithLeaves = useMemo(() =>
    Object.entries(leaveCounts)
      .map(([sid, count]) => ({ sid, count, name: (staffLookup[sid] || {}).name || '(deleted staff)' }))
      .sort((a, b) => b.count - a.count),
    [leaveCounts, staffLookup]
  );

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      {/* Date filter */}
      <Card className="p-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-slate-500">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => onDateFromChange(e.target.value)}
            className="w-[145px]" data-testid="leave-from" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => onDateToChange(e.target.value)}
            className="w-[145px]" data-testid="leave-to" />
        </div>
        <Button size="sm" onClick={onRefresh} data-testid="leave-search-btn">Search</Button>
      </Card>

      {/* Leave Tracker summary */}
      {staffWithLeaves.length > 0 && (
        <Card className="p-3" data-testid="leave-tracker">
          <div className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Leave Count by Staff</div>
          <div className="flex flex-wrap gap-2">
            {staffWithLeaves.map(({ sid, count, name }) => (
              <div key={sid}
                className="flex items-center gap-2 bg-rose-50 border border-rose-100 rounded-lg px-3 py-1.5"
                data-testid={`leave-count-${sid}`}>
                <span className="text-sm font-bold text-rose-600">{count}</span>
                <span className="text-xs text-slate-700">{name}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Leave table */}
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-slate-500 text-xs uppercase tracking-wide">
              <th className="px-3 py-3">Staff</th>
              <th className="px-3 py-3">Venue</th>
              <th className="px-3 py-3">Date</th>
              <th className="px-3 py-3">Day</th>
              <th className="px-3 py-3">Reason</th>
            </tr>
          </thead>
          <tbody>
            {enriched.length === 0
              ? <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400 text-sm">No leave records found.</td></tr>
              : enriched.map((l, i) => (
                <tr key={i} className="border-b hover:bg-slate-50/60" data-testid={`leave-row-${i}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{l.staff_name}</td>
                  <td className="px-3 py-2 text-slate-500">{l.venue || '—'}</td>
                  <td className="px-3 py-2">{l.date}</td>
                  <td className="px-3 py-2 uppercase text-xs font-semibold text-slate-500">{l.day}</td>
                  <td className="px-3 py-2 text-slate-500">{l.leave_reason || '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STAFF MASTER PANEL
// ══════════════════════════════════════════════════════════════════════════════

function StaffMasterPanel({ staffList, loading, venueNames, monthlyHours, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = useMemo(() => staffList.filter((s) => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [staffList, statusFilter, search]);

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await axios.delete(`${API}/duty-roster/staff/${s.id}`);
      toast.success('Deleted'); onChanged();
    } catch (e) { toast.error(err(e)); }
  };

  return (
    <div className="flex-1 overflow-auto p-4 space-y-3">
      <Card className="p-3 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Search</Label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name…" />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-2 bg-white w-[130px] focus:outline-none focus:ring-1 focus:ring-indigo-300"
            data-testid="staff-filter-status">
            <option value="all">All</option>
            {STAFF_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white ml-auto"
          data-testid="add-staff-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Staff
        </Button>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: 900 }}>
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-slate-500 text-xs uppercase tracking-wide">
              {['Name', 'Mobile', 'Designation', 'Default Venue', 'Status', 'Joined', 'Salary/mo', 'This Month Hrs', 'KYC', ''].map((h) => (
                <th key={h} className="px-3 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={10} className="px-3 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-300" /></td></tr>
              : filtered.length === 0
              ? <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">No staff found.</td></tr>
              : filtered.map((s) => (
                <tr key={s.id} className="border-b hover:bg-slate-50/60" data-testid={`staff-row-${s.id}`}>
                  <td className="px-3 py-2 font-semibold text-slate-800">{s.name}</td>
                  <td className="px-3 py-2 text-slate-500">{s.mobile || '—'}</td>
                  <td className="px-3 py-2 text-slate-500">{s.designation || '—'}</td>
                  <td className="px-3 py-2 text-slate-500">{s.venue || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[s.status] || 'bg-slate-100'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-500">{s.joining_date || '—'}</td>
                  <td className="px-3 py-2 text-slate-700 text-right">
                    {s.monthly_salary > 0 ? `₹${Number(s.monthly_salary).toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {monthlyHours[s.id] != null
                      ? <span className="font-semibold text-indigo-700">{monthlyHours[s.id]}h</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {s.kyc_url
                      ? <a href={s.kyc_url} target="_blank" rel="noopener noreferrer"
                          className="text-indigo-600 hover:underline text-xs flex items-center gap-1"
                          data-testid={`kyc-${s.id}`}>
                          <ExternalLink className="w-3 h-3" /> View
                        </a>
                      : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => { setEditing(s); setShowForm(true); }}
                      className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                      data-testid={`edit-staff-${s.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(s)}
                      className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors"
                      data-testid={`delete-staff-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>

      {showForm && (
        <StaffFormDialog initial={editing} venueNames={venueNames}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); onChanged(); }} />
      )}
    </div>
  );
}

function StaffFormDialog({ initial, venueNames, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial || {
    name: '', mobile: '', designation: '', venue: '', joining_date: '',
    leaving_date: '', status: 'active', monthly_salary: '', kyc_url: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name?.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      if (isEdit) await axios.put(`${API}/duty-roster/staff/${initial.id}`, form);
      else await axios.post(`${API}/duty-roster/staff`, form);
      toast.success(isEdit ? 'Updated' : 'Staff added');
      onSaved();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="staff-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Staff' : 'Add Staff Member'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Full Name *</Label>
            <Input value={form.name || ''} onChange={(e) => set('name', e.target.value)} data-testid="staff-name-input" />
          </div>
          <div>
            <Label>Mobile</Label>
            <Input value={form.mobile || ''} onChange={(e) => set('mobile', e.target.value)} data-testid="staff-mobile-input" />
          </div>
          <div>
            <Label>Designation</Label>
            <Input value={form.designation || ''} onChange={(e) => set('designation', e.target.value)} placeholder="e.g. Court Manager" data-testid="staff-designation-input" />
          </div>
          <div>
            <Label>Default Venue</Label>
            <select value={form.venue || ''} onChange={(e) => set('venue', e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              data-testid="staff-venue-select">
              <option value="">— Optional —</option>
              {venueNames.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <Label>Status</Label>
            <select value={form.status || 'active'} onChange={(e) => set('status', e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              data-testid="staff-status-select">
              {STAFF_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label>Joining Date</Label>
            <Input type="date" value={form.joining_date || ''} onChange={(e) => set('joining_date', e.target.value)} data-testid="staff-joining-date" />
          </div>
          <div>
            <Label>Leaving Date</Label>
            <Input type="date" value={form.leaving_date || ''} onChange={(e) => set('leaving_date', e.target.value)} data-testid="staff-leaving-date" />
          </div>
          <div>
            <Label>Monthly Salary (₹)</Label>
            <Input type="number" min="0" value={form.monthly_salary || ''} onChange={(e) => set('monthly_salary', e.target.value)} data-testid="staff-salary-input" />
          </div>
          <div className="col-span-2">
            <Label>KYC Document URL</Label>
            <Input value={form.kyc_url || ''} onChange={(e) => set('kyc_url', e.target.value)} placeholder="Paste Google Drive share link" data-testid="staff-kyc-url" />
            <p className="text-[10px] text-slate-400 mt-0.5">Paste Drive / cloud link for Aadhaar, PAN, etc.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="staff-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {isEdit ? 'Save Changes' : 'Add Staff'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SALARY REPORT PANEL
// ══════════════════════════════════════════════════════════════════════════════

function SalaryReportPanel() {
  const [month, setMonth] = useState(currentMonthISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deductions, setDeductions] = useState({});  // staffId → amount
  const [additions, setAdditions] = useState({});    // staffId → amount

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/duty-roster/salary-report`, { params: { month } });
      setRows(r.data.rows || []);
    } catch (e) { toast.error(err(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [month]);

  const setDeduction = (id, val) => setDeductions((d) => ({ ...d, [id]: parseFloat(val) || 0 }));
  const setAddition  = (id, val) => setAdditions((a) => ({ ...a, [id]: parseFloat(val) || 0 }));

  const computedRows = rows.map((r) => {
    const ded = deductions[r.staff_id] || 0;
    const add = additions[r.staff_id] || 0;
    return { ...r, deductions: ded, additions: add, net_pay: Math.max(0, r.earned_salary + add - ded) };
  });

  const exportSalary = () => {
    const headers = ['#', 'Name', 'Designation', 'Venue', 'Status',
      'Working Days', 'Weekoff Days', 'Leave Days', 'Total Hours',
      'Monthly Salary', 'Per Day (÷26)', 'Earned Salary', 'Additions', 'Deductions', 'Net Pay'];
    const rowData = computedRows.map((r, i) => [
      i + 1, r.name, r.designation, r.venue, r.status,
      r.working_days, r.weekoff_days, r.leave_days, r.total_hours,
      r.monthly_salary, r.per_day_salary, r.earned_salary,
      r.additions, r.deductions, r.net_pay,
    ]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rowData]);
    XLSX.utils.book_append_sheet(wb, ws, `Salary ${month}`);
    XLSX.writeFile(wb, `SalaryReport_${month}.xlsx`);
  };

  const total = computedRows.reduce((a, r) => ({
    earned: a.earned + r.earned_salary,
    add: a.add + r.additions,
    ded: a.ded + r.deductions,
    net: a.net + r.net_pay,
  }), { earned: 0, add: 0, ded: 0, net: 0 });

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4" data-testid="salary-report-panel">
      <Card className="p-3 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="w-[160px]" data-testid="salary-month-input" />
        </div>
        <Button size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Generate
        </Button>
        <p className="text-xs text-slate-400 self-center ml-1">Salary ÷ 26 = 1 day's pay</p>
        {rows.length > 0 && (
          <Button size="sm" variant="outline" onClick={exportSalary} className="ml-auto" data-testid="salary-export-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Export Excel
          </Button>
        )}
      </Card>

      {/* Summary KPIs */}
      {computedRows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Staff', value: computedRows.length, color: 'text-slate-700' },
            { label: 'Total Earned', value: `₹${total.earned.toLocaleString('en-IN')}`, color: 'text-emerald-600' },
            { label: 'Total Deductions', value: `₹${total.ded.toLocaleString('en-IN')}`, color: 'text-rose-600' },
            { label: 'Total Net Pay', value: `₹${total.net.toLocaleString('en-IN')}`, color: 'text-indigo-700' },
          ].map(({ label, value, color }) => (
            <Card key={label} className="p-3 text-center">
              <div className="text-xs text-slate-500">{label}</div>
              <div className={`text-lg font-bold mt-0.5 ${color}`}>{value}</div>
            </Card>
          ))}
        </div>
      )}

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-xs" style={{ minWidth: 1000 }}>
          <thead className="bg-slate-800 text-white">
            <tr>
              {['#', 'Name', 'Desig.', 'Days', 'Off', 'Leave', 'Hours',
                'Monthly Salary', 'Per Day (÷26)', 'Earned',
                'Additions (+)', 'Deductions (−)', 'Net Pay'].map((h) => (
                <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={13} className="px-3 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-300" /></td></tr>
              : computedRows.length === 0
              ? <tr><td colSpan={13} className="px-3 py-8 text-center text-slate-400">No data for this month. Publish a roster first.</td></tr>
              : computedRows.map((r, i) => (
                <tr key={r.staff_id} className={`border-b ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
                  data-testid={`salary-row-${r.staff_id}`}>
                  <td className="px-2.5 py-2 text-slate-400">{i + 1}</td>
                  <td className="px-2.5 py-2 font-semibold text-slate-800">{r.name}</td>
                  <td className="px-2.5 py-2 text-slate-500">{r.designation || '—'}</td>
                  <td className="px-2.5 py-2 text-center font-semibold text-emerald-700">{r.working_days}</td>
                  <td className="px-2.5 py-2 text-center text-indigo-600">{r.weekoff_days}</td>
                  <td className="px-2.5 py-2 text-center text-rose-500">{r.leave_days}</td>
                  <td className="px-2.5 py-2 text-center text-slate-600">{r.total_hours}h</td>
                  <td className="px-2.5 py-2 text-right">₹{Number(r.monthly_salary).toLocaleString('en-IN')}</td>
                  <td className="px-2.5 py-2 text-right text-slate-500">₹{Number(r.per_day_salary).toLocaleString('en-IN')}</td>
                  <td className="px-2.5 py-2 text-right font-semibold text-emerald-700">₹{Number(r.earned_salary).toLocaleString('en-IN')}</td>
                  <td className="px-2.5 py-2 text-center">
                    <input type="number" min="0"
                      value={additions[r.staff_id] ?? 0}
                      onChange={(e) => setAddition(r.staff_id, e.target.value)}
                      className="w-[72px] text-xs border border-emerald-200 rounded px-1.5 py-1 text-center focus:outline-none focus:ring-1 focus:ring-emerald-300 bg-emerald-50"
                      data-testid={`addition-${r.staff_id}`} />
                  </td>
                  <td className="px-2.5 py-2 text-center">
                    <input type="number" min="0"
                      value={deductions[r.staff_id] ?? 0}
                      onChange={(e) => setDeduction(r.staff_id, e.target.value)}
                      className="w-[72px] text-xs border border-rose-200 rounded px-1.5 py-1 text-center focus:outline-none focus:ring-1 focus:ring-rose-300 bg-rose-50"
                      data-testid={`deduction-${r.staff_id}`} />
                  </td>
                  <td className="px-2.5 py-2 text-right font-bold text-indigo-700">₹{Number(r.net_pay).toLocaleString('en-IN')}</td>
                </tr>
              ))}
          </tbody>
          {computedRows.length > 0 && (
            <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-bold text-xs">
              <tr className="text-slate-700">
                <td colSpan={9} className="px-2.5 py-2 text-right">Totals →</td>
                <td className="px-2.5 py-2 text-right text-emerald-700">₹{total.earned.toLocaleString('en-IN')}</td>
                <td className="px-2.5 py-2 text-right text-emerald-700">₹{total.add.toLocaleString('en-IN')}</td>
                <td className="px-2.5 py-2 text-right text-rose-600">₹{total.ded.toLocaleString('en-IN')}</td>
                <td className="px-2.5 py-2 text-right text-indigo-700">₹{total.net.toLocaleString('en-IN')}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SETTINGS PANEL
// ══════════════════════════════════════════════════════════════════════════════

function SettingsPanel({ settings, onSaved }) {
  const [closingTime, setClosingTime] = useState(settings.closing_time || '00:30');
  const [saving, setSaving] = useState(false);
  useEffect(() => setClosingTime(settings.closing_time || '00:30'), [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/duty-roster/settings`, { closing_time: closingTime });
      toast.success('Settings saved');
      onSaved && onSaved(r.data);
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex-1 overflow-auto p-4">
      <Card className="p-4 max-w-sm space-y-4" data-testid="duty-settings-card">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-slate-400" /> Roster Settings
        </h3>
        <div>
          <Label>Closing Time (HH:MM, 24-hour)</Label>
          <Input value={closingTime} onChange={(e) => setClosingTime(e.target.value)}
            placeholder="00:30" className="max-w-[120px] mt-1" data-testid="closing-time-input" />
          <p className="text-xs text-slate-400 mt-1">
            Shifts ending at "closing" use this time. Default: 00:30 (12:30 AM).
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save Settings
        </Button>
      </Card>
    </div>
  );
}
